import { STATE } from '../../constants';
import { default as actions } from '../';
import { default as idVerifySelector } from '../../selectors/idVerify';

/**
 * This "action" is a thunk of sorts. It is intended to initiate the ID Verify service's Code. In 
 * the case of Acuant this means an async call is fired from the SDK's WebAssembly code to validate
 * the Acuant credentials. On success the SDK is fully initialized/functional and the 
 * `onInitSuccess` callback is called. If the request fail the `onInitFail` callback is called
 * instead.
 *
 * @param {object} config - an object of configuration properties for initializing the SDK
 * @param {function} config.onInitFail - (optional) callback that's fired when SDK init fails
 * @param {function} config.onInitSuccess - (optional) callback that's fired when SDK init succeeds
 * @param {string} config.sdkEndpoint - the SDK's authentication URL
 * @param {string} config.sdkFileName - (optional) the name of the SDK's source file in `sdkFolderPath`
 * @param {string} config.sdkFolderPath - a (publically accessible) root relative path to the SDK code
 * @param {string} config.sdkPassword - the SDK authentication username
 * @param {string} config.sdkUsername - the SDK authentication password
 *
 * @return {function} a standard thunk function allowing for additional sync/async logic to be called
 */
export const initIdVerifySdk = ({
  onInitFail,
  onInitSuccess,
  sdkEndpoint,
  sdkFileName = 'AcuantJavascriptWebSdk.min.js',
  sdkFolderPath,
  sdkPassword,
  sdkUsername
}) => (dispatch, getState) => {
  // Make sure we have all the required values. Otherwise get out! 
  if([sdkEndpoint, sdkFolderPath, sdkPassword, sdkUsername].some(required => !required)) return;

  // Function to call when the SDK initializes
  const initializeAcuant = () => {
    const state = getState();
    const acuantSdkLoading = idVerifySelector.sdkLoading(state);
    const acuantSdkInitialized = idVerifySelector.sdkInitialized(state);

    // Make sure the init is only while it is not loading (prevent multiple concurrent attempts to init)
    if (!acuantSdkLoading && !acuantSdkInitialized) {
      dispatch(actions.setGmiState(STATE.UI.ID_VERIFY.SDK_LOADING, true));

      // The token has to be encrypted (per Acuant "spec"). For more information on the `btoa` method
      // @see https://developer.mozilla.org/en-US/docs/Web/API/WindowOrWorkerGlobalScope/btoa
      const base64Token = btoa(
        sdkUsername + ':' + sdkPassword
      );

      // Trigger the actual SDK init function. This fires an async request to the Acuant server and
      // calls a `success` or `fail` callback based on the response.
      window.AcuantJavascriptWebSdk.initialize(base64Token, sdkEndpoint, {
        onSuccess: () => {
          dispatch(actions.setGmiState(STATE.UI.ID_VERIFY.SDK_LOADING, false));
          dispatch(actions.setGmiState(STATE.UI.ID_VERIFY.SDK_INITIALIZED, true));
          if (onInitSuccess) onInitSuccess();
        },
        onFail: () => {
          dispatch(actions.setGmiState(STATE.UI.ID_VERIFY.SDK_LOADING, false));
          if (onInitFail) onInitFail();
        }
      });
    }
  };

  // If the SDK's JS has not yet been loaded, add the JS file (`<script />`) to the DOM.
  if (!window.AcuantJavascriptWebSdk) {
    // Set acuant config used by SDK
    window.acuantConfig = { path: sdkFolderPath };
    window.onAcuantSdkLoaded = initializeAcuant;

    // Load the SDK
    const sdk = document.createElement('script');
    sdk.src = `${sdkFolderPath}${sdkFileName}`;
    sdk.async = true;
    document.body.appendChild(sdk);
  // If the SDK is already included, just run the initialize function...
  } else{
    initializeAcuant();
  }
};
